// Mediavine provider (2026-09-29). Mediavine's script wrapper owns placement:
// ad positions are configured in the Mediavine dashboard and the wrapper
// inserts them itself, so renderUnit never fills one of our hosts. The wrapper
// can't be unloaded once injected, so callers must only load it for signed-out
// visitors AFTER the session check has settled (see placements.ts).
// Mediavine requires exclusivity: never load another display network with it.
import { adsConfig } from './../config'
import { detectAdblockBait } from './../adblock'
import type { AdProvider, FillResult, LoadResult } from './types'

let sdkPromise: Promise<LoadResult> | null = null

/** True when the page was built with Mediavine's wrapper tag in its HTML. */
export function hasStaticMediavineTag(): boolean {
  return !!document.querySelector('script[src*="scripts.mediavine.com/tags/"]')
}

/** Mediavine can't be switched off or on mid-page: after a sign-in/out a reload
 *  lets the head marker (vite.config.ts) apply the right state. */
export function reloadForAdState(): boolean {
  if (!hasStaticMediavineTag()) return false
  window.location.reload()
  return true
}

function loadSdk(): Promise<LoadResult> {
  const { mediavineSiteId, sdkTimeoutMs } = adsConfig()
  if (!mediavineSiteId) return Promise.resolve('timeout')
  if (sdkPromise) return sdkPromise
  // Production builds carry the wrapper as a static <head> tag (vite.config.ts);
  // never add a second one.
  if (hasStaticMediavineTag()) return (sdkPromise = Promise.resolve('ok'))
  sdkPromise = new Promise<LoadResult>((resolve) => {
    let done = false
    const finish = (r: LoadResult) => {
      if (!done) { done = true; resolve(r) }
      if (r !== 'ok') sdkPromise = null
    }
    const s = document.createElement('script')
    s.type = 'text/javascript'
    s.async = true
    s.setAttribute('data-noptimize', '1')
    s.setAttribute('data-cfasync', 'false')
    s.src = `https://scripts.mediavine.com/tags/${encodeURIComponent(mediavineSiteId)}.js`
    s.onerror = () => finish(navigator.onLine ? 'blocked' : 'timeout')
    s.onload = () => finish('ok')
    document.head.appendChild(s)
    setTimeout(() => finish('timeout'), sdkTimeoutMs)
  })
  return sdkPromise
}

/** Mediavine places its own units — nothing to render into our hosts. */
function renderUnit(): Promise<FillResult> {
  return Promise.resolve('unfilled')
}

export const mediavineProvider: AdProvider = {
  id: 'mediavine',
  loadSdk,
  renderUnit,
  detectAdblock: detectAdblockBait,
}
